import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAgendaDock } from './AgendaDockContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  MessageCircle, X, Minus, Hash, Send, Paperclip,
  ChevronDown, Loader2, ShieldAlert, Maximize2, Minimize2,
  ArrowLeftRight, ArrowUpDown, Volume2, VolumeX, Bell, UserPlus, User,
  Users, MoreVertical, Trash2, Eraser, Flag, IdCard, Move, Pin, PinOff, Reply,
  CalendarDays, PanelRight, PictureInPicture2, Expand, Shrink, StickyNote, ClipboardPlus,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useClinic } from '@/stores/ClinicContext';
import { formatTimeHM } from '@/utils/date';
import {
  ChatService,
  buildReplyContext,
  type ChatMessageData,
  type ChatChannelData,
  type ClinicMemberData,
  type PendingMessageData,
} from '@/services/firebase/chat';
import { PendingMessagesPanel } from '@/components/chat/PendingMessagesPanel';
import {
  playChatPing,
  playChatAttentionPing,
  isChatSoundEnabled,
  setChatSoundEnabled,
  startTitleFlash,
  stopTitleFlash,
} from '@/utils/chatAlert';
import {
  getChannelDisplayName,
  isVisibleChannel,
  membersToLookup,
} from '@/utils/chatChannel';
import { CitePatientButton } from '@/components/chat/CitePatientButton';
import { ChatToMuralModal, canPinToMural, useCanPinToMural } from '@/components/chat/ChatToMuralModal';
import { ChatToRecordModal, useCanRegisterChatInRecord } from '@/components/chat/ChatToRecordModal';
import { canRegisterChatInRecord } from '@/utils/chatToRecord';
import { CHAT_CITE_PATIENT_EVENT } from '@/utils/chatCite';
import type { Patient } from '@/types/models';

type ChatPosition = 'left' | 'center' | 'right';
const POSITION_STORAGE_KEY = 'dratento-chat-position';

const posClasses: Record<ChatPosition, string> = {
  left: 'left-6 md:left-72',
  center: 'left-1/2 -translate-x-1/2',
  right: 'right-6',
};

const posLabels: Record<ChatPosition, string> = {
  left: 'Esquerda',
  center: 'Centro',
  right: 'Direita',
};

// Âncora vertical — em qual borda da tela o chat fica grudado (topo ou rodapé).
// É um eixo independente do horizontal acima; juntos cobrem os 6 cantos/posições.
type ChatVertical = 'top' | 'bottom';
const VERTICAL_STORAGE_KEY = 'dratento-chat-vertical';
const vertLabels: Record<ChatVertical, string> = {
  top: 'Em cima',
  bottom: 'Embaixo',
};

// Posição livre (arrastada) — sobrepõe as âncoras left/center/right × top/bottom
// quando definida. Guarda o canto superior-esquerdo em px. Escolher um preset no
// menu limpa isto (volta a ancorar). Persistida em localStorage.
type CustomPos = { x: number; y: number };
const CUSTOM_POS_STORAGE_KEY = 'dratento-chat-custom-pos';
// "Manter sempre aberto": não minimiza ao clicar fora e abre sozinho ao montar.
const ALWAYS_OPEN_STORAGE_KEY = 'dratento-chat-always-open';
// Chat NO PAINEL LATERAL (encaixado ao lado da agenda) em vez de flutuar por
// cima da tela. Padrão: ligado. "0" = o usuário preferiu o balão flutuante.
const IN_DOCK_STORAGE_KEY = 'dratento-chat-in-dock';
// Alt + esta tecla física abre/fecha o chat no painel (Alt+A agenda, Alt+R resumo).
const DOCK_TOGGLE_CODE = 'KeyC';

const DRAG_THRESHOLD = 4; // px de movimento p/ distinguir clique de arraste

/** Mantém (x,y) dentro da viewport, considerando o tamanho do elemento. */
function clampToViewport(x: number, y: number, w: number, h: number): CustomPos {
  const maxX = Math.max(0, window.innerWidth - w);
  const maxY = Math.max(0, window.innerHeight - h);
  return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) };
}

function readCustomPos(): CustomPos | null {
  try {
    const raw = localStorage.getItem(CUSTOM_POS_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<CustomPos>;
    if (typeof p?.x === 'number' && typeof p?.y === 'number') return { x: p.x, y: p.y };
  } catch {
    // localStorage corrompido — ignora e volta às âncoras
  }
  return null;
}

type PanelState = 'closed' | 'minimized' | 'open' | 'expanded';

export const FloatingChat = () => {
  const { user, profile } = useAuth();
  const { clinicId, isOwner } = useClinic();

  const uid = user?.uid || '';
  const userName = useMemo(
    () => user?.displayName || profile?.email || 'Usuário',
    [user?.displayName, profile?.email],
  );

  // Abre direto quando "manter sempre aberto" está ligado (lido só no 1º mount).
  const [state, setState] = useState<PanelState>(() =>
    localStorage.getItem(ALWAYS_OPEN_STORAGE_KEY) === '1' ? 'open' : 'closed',
  );
  // MODO POP-UP: o chat sai do lugar de sempre (balão flutuante OU painel
  // lateral) e vira um pop-up grande no CENTRO da tela, com fundo escurecido
  // — pra ler/escrever com folga. É um modo temporário: não é persistido, e
  // sair dele devolve o chat exatamente pro lugar de onde veio.
  const [popup, setPopup] = useState(false);
  const [channels, setChannels] = useState<ChatChannelData[]>([]);
  const [clinicMembers, setClinicMembers] = useState<ClinicMemberData[]>([]);
  const [showNewDmList, setShowNewDmList] = useState(false);
  const [openingDm, setOpeningDm] = useState(false);
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupMembers, setNewGroupMembers] = useState<Record<string, boolean>>({});
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: 'message'; messageId: string }
    | { kind: 'clearChannel' }
    | { kind: 'deleteChannel' }
    | null
  >(null);
  const [working, setWorking] = useState(false);
  const [activeChannel, setActiveChannel] = useState('');
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showChannelList, setShowChannelList] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingItems, setPendingItems] = useState<PendingMessageData[]>([]);
  const [showPending, setShowPending] = useState(false);
  const [resolvingPending, setResolvingPending] = useState<string | null>(null);
  const [position, setPosition] = useState<ChatPosition>(() => {
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);
    return (saved === 'left' || saved === 'center' || saved === 'right') ? saved : 'right';
  });
  const [vertical, setVertical] = useState<ChatVertical>(() => {
    const saved = localStorage.getItem(VERTICAL_STORAGE_KEY);
    return saved === 'top' ? 'top' : 'bottom';
  });
  const [showPositionMenu, setShowPositionMenu] = useState(false);
  // Posição livre por arraste (sobrepõe as âncoras quando não-null).
  const [customPos, setCustomPos] = useState<CustomPos | null>(() => readCustomPos());
  const [isDragging, setIsDragging] = useState(false);
  const [alwaysOpen, setAlwaysOpen] = useState<boolean>(
    () => localStorage.getItem(ALWAYS_OPEN_STORAGE_KEY) === '1',
  );
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(() => isChatSoundEnabled());
  // Incrementa a cada mensagem recebida → força replay da animação de flash
  const [flashKey, setFlashKey] = useState(0);
  // Paciente "citado" anexado à próxima mensagem (vínculo clicável).
  const [citedPatient, setCitedPatient] = useState<Patient | null>(null);
  // "Responder a": mensagem citada pela próxima mensagem + destaque temporário
  // da original quando o usuário clica na citação.
  const [replyingTo, setReplyingTo] = useState<ChatMessageData | null>(null);
  const [highlightedMsgId, setHighlightedMsgId] = useState<string | null>(null);
  // "Colar no mural": mensagem que está virando post-it.
  const [muralFrom, setMuralFrom] = useState<ChatMessageData | null>(null);
  const canPinMural = useCanPinToMural();
  // "Registrar no prontuário": mensagem que está virando evolução na ficha.
  const [recordFrom, setRecordFrom] = useState<ChatMessageData | null>(null);
  const canRegisterRecord = useCanRegisterChatInRecord();
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ── Chat no painel lateral direito ──────────────────────────────────
  // Quando há um hospedeiro do painel nesta rota (DayAgendaDrawer fora da
  // /agenda) e a tela é de desktop, o chat vira uma VISTA do painel: ganha
  // ícone no trilho (com não-lidas), abre no mesmo lugar da agenda e nunca
  // cobre a ficha. Sem hospedeiro (ou por preferência), volta a flutuar.
  const dock = useAgendaDock();
  const [chatInDock, setChatInDockState] = useState<boolean>(() => {
    try { return localStorage.getItem(IN_DOCK_STORAGE_KEY) !== '0'; } catch { return true; }
  });
  const setChatInDock = useCallback((v: boolean) => {
    try { localStorage.setItem(IN_DOCK_STORAGE_KEY, v ? '1' : '0'); } catch { /* sem storage */ }
    setChatInDockState(v);
  }, []);
  const wideScreen = useMediaQuery('(min-width: 640px)');
  const docked = chatInDock && dock.hostMounted && wideScreen && location.pathname !== '/chat';
  // Na própria /agenda o painel não tem vista de agenda (a agenda inteira já
  // está na tela) — só o chat mora nele.
  const onAgendaPage = location.pathname === '/agenda';
  // Ficha aberta na tela — paciente sugerido ao registrar mensagem sem vínculo.
  const openPatientId = location.pathname.match(/^\/pacientes\/([^/?]+)/)?.[1];
  const dockedVisible = docked && dock.open && dock.view === 'chat';
  const { registerView, unregisterView, setViewBadge, openView, closeDock, toggleView, slotEl } = dock;

  // Registra a vista "chat" no trilho enquanto estiver encaixado.
  useEffect(() => {
    if (!docked) return;
    registerView('chat', { label: 'Chat' });
    return () => unregisterView('chat');
  }, [docked, registerView, unregisterView]);
  // Não-lidas → contador no ícone do trilho.
  useEffect(() => {
    if (docked) setViewBadge('chat', unreadCount);
  }, [docked, unreadCount, setViewBadge]);
  // Encaixado, o "estado" do painel é o do dock: aberto quando a vista chat
  // está à mostra, fechado caso contrário. Mantém funcionando os efeitos que
  // dependem de `state` (marcar como lido, auto-scroll, contador).
  // No pop-up o chat saiu do painel — o estado dele não é mais o do dock.
  useEffect(() => {
    if (docked && !popup) setState(dockedVisible ? 'open' : 'closed');
  }, [docked, dockedVisible, popup]);

  // Entrar/sair do pop-up. Vindo do painel lateral, recolhe o painel (senão
  // ele fica ali vazio atrás do fundo escurecido) e reabre a vista do chat na
  // volta — o chat retorna exatamente pro lugar de onde saiu.
  const openPopup = useCallback(() => {
    // Na /chat o chat inteiro já é a tela — pop-up ali não faz sentido.
    if (location.pathname === '/chat') return;
    setPopup(true);
    setState('open');
    if (docked) closeDock();
  }, [docked, closeDock, location.pathname]);
  const closePopup = useCallback(() => {
    setPopup(false);
    if (docked) openView('chat');
  }, [docked, openView]);

  // A subscription de alertas não re-assina a cada render (só quando muda a
  // lista de canais), então ela chama o pop-up por REF — assim usa sempre a
  // versão atual da função, sem fechar sobre um `docked` velho.
  const openPopupRef = useRef(openPopup);
  useEffect(() => { openPopupRef.current = openPopup; }, [openPopup]);

  // Esc fecha o pop-up (volta o chat pro lugar de origem).
  useEffect(() => {
    if (!popup) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); closePopup(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popup, closePopup]);
  // Alt+C abre/fecha o chat no painel (só encaixado — flutuando, o balão
  // já está sempre à mão).
  useEffect(() => {
    if (!docked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === DOCK_TOGGLE_CODE) {
        e.preventDefault();
        toggleView('chat');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docked, toggleView]);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastSeenRef = useRef<number>(Date.now());
  // Refs pra ler `state` e `activeChannel` de dentro de callbacks de
  // subscription sem precisar re-subscrever a cada mudança — assim mensagens
  // que chegam exatamente quando o estado muda não viram "perdidas" no reset.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);
  const activeChannelRef = useRef('');
  useEffect(() => { activeChannelRef.current = activeChannel; }, [activeChannel]);
  // Espelho do canto atual (pra persistir no mouseup sem closure stale) +
  // flags de arraste em curso / supressão do clique que segue um arraste.
  const customPosRef = useRef<CustomPos | null>(customPos);
  useEffect(() => { customPosRef.current = customPos; }, [customPos]);
  const isDraggingRef = useRef(false);
  const suppressClickRef = useRef(false);

  // Bootstrap assim que tivermos clinicId + uid — mesmo com o chat ainda
  // só no balão. Precisamos da subscription de mensagens viva pra disparar
  // o alerta (badge, flash e som) quando chega mensagem nova antes da
  // secretária abrir o painel.
  const hasBootstrapped = useRef(false);

  useEffect(() => {
    if (!clinicId || !uid || hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    (async () => {
      try {
        await ChatService.ensureClinicMember(clinicId, uid, userName);
        await ChatService.seedDefaultChannels(clinicId, uid);
        // Garante membership em TODOS os canais públicos — sem isso, quem
        // entrou na clínica depois do seed inicial não recebe alertas até
        // trocar manualmente para o canal (regras do RTDB bloqueiam o read).
        await ChatService.joinAllPublicChannels(clinicId, uid);
        setIsReady(true);
      } catch (err) {
        console.error('FloatingChat bootstrap error:', err);
      }
    })();
  }, [clinicId, uid, userName]);

  // Subscribe to channels (filtra DMs onde o usuário não é participante)
  useEffect(() => {
    if (!clinicId || !isReady || !uid) return;
    return ChatService.subscribeToChannels(clinicId, (list) => {
      const visible = list.filter((c) => isVisibleChannel(c, uid));
      setChannels(visible);
      setActiveChannel((prev) => {
        if (prev && visible.some((c) => c.id === prev)) return prev;
        // Prefere o primeiro canal público em vez de uma DM aleatória
        const firstPublic = visible.find((c) => c.type !== 'dm');
        return firstPublic?.id || visible[0]?.id || '';
      });
    });
  }, [clinicId, isReady, uid]);

  // Subscribe to clinic members (para o seletor de "Nova conversa privada")
  useEffect(() => {
    if (!clinicId || !isReady) return;
    return ChatService.subscribeToClinicMembers(clinicId, setClinicMembers);
  }, [clinicId, isReady]);

  const clinicMembersLookup = useMemo(() => membersToLookup(clinicMembers), [clinicMembers]);

  // Pendências de resposta — compartilhadas, agrupadas por conversa. Assina a
  // árvore inteira e filtramos pelas conversas que este usuário enxerga.
  useEffect(() => {
    if (!clinicId || !isReady) return;
    return ChatService.subscribeToPending(clinicId, setPendingItems);
  }, [clinicId, isReady]);

  const visiblePending = useMemo(() => {
    const visibleIds = new Set(channels.map((c) => c.id));
    return pendingItems.filter((p) => visibleIds.has(p.channelId));
  }, [pendingItems, channels]);

  const activePendingIds = useMemo(
    () => new Set(
      visiblePending.filter((p) => p.channelId === activeChannel).map((p) => p.messageId),
    ),
    [visiblePending, activeChannel],
  );

  const togglePending = useCallback(async (m: ChatMessageData) => {
    if (!clinicId) return;
    const channelId = m.channelId || activeChannel;
    const already = pendingItems.some((p) => p.channelId === channelId && p.messageId === m.id);
    try {
      if (already) {
        await ChatService.resolvePending(clinicId, channelId, m.id);
      } else {
        await ChatService.markMessagePending(clinicId, { ...m, channelId }, uid, userName);
      }
    } catch (err) {
      console.error('[chat] togglePending falhou', err);
      alert('Não foi possível atualizar a pendência.');
    }
  }, [clinicId, activeChannel, pendingItems, uid, userName]);

  const goToPending = useCallback((channelId: string) => {
    setActiveChannel(channelId);
    setShowPending(false);
    setShowChannelList(false);
  }, []);

  const resolveFromPanel = useCallback(async (channelId: string, messageId: string) => {
    if (!clinicId) return;
    const key = `${channelId}/${messageId}`;
    setResolvingPending(key);
    try {
      await ChatService.resolvePending(clinicId, channelId, messageId);
    } catch (err) {
      console.error('[chat] resolvePending falhou', err);
      alert('Não foi possível resolver a pendência.');
    } finally {
      setResolvingPending(null);
    }
  }, [clinicId]);

  const pendingChannelLabel = useCallback((channelId: string) => {
    const ch = channels.find((c) => c.id === channelId);
    if (!ch) return { name: channelId, isDm: false };
    return { name: getChannelDisplayName(ch, uid, clinicMembersLookup), isDm: ch.type === 'dm' };
  }, [channels, uid, clinicMembersLookup]);

  const handleStartDm = useCallback(async (otherUid: string, otherName: string) => {
    if (!clinicId || !uid || openingDm || otherUid === uid) return;
    setOpeningDm(true);
    try {
      const channelId = await ChatService.openDirectMessage(
        clinicId,
        uid,
        userName,
        otherUid,
        otherName,
      );
      setActiveChannel(channelId);
      setShowNewDmList(false);
      setShowChannelList(false);
    } catch (err) {
      console.error('Erro ao abrir conversa privada:', err);
      alert('Não foi possível abrir a conversa privada.');
    } finally {
      setOpeningDm(false);
    }
  }, [clinicId, uid, userName, openingDm]);

  const resetNewGroupForm = useCallback(() => {
    setShowNewGroupForm(false);
    setNewGroupName('');
    setNewGroupMembers({});
  }, []);

  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name || !clinicId || !uid || creatingGroup) return;
    const additional = Object.entries(newGroupMembers)
      .filter(([, on]) => on)
      .map(([id]) => id);
    if (additional.length === 0) {
      alert('Selecione pelo menos um membro para o grupo.');
      return;
    }
    setCreatingGroup(true);
    try {
      const channelId = await ChatService.createChannel(clinicId, name, uid, additional);
      setActiveChannel(channelId);
      resetNewGroupForm();
      setShowChannelList(false);
    } catch (err) {
      console.error('Erro ao criar grupo:', err);
      alert('Não foi possível criar o grupo.');
    } finally {
      setCreatingGroup(false);
    }
  }, [clinicId, uid, newGroupName, newGroupMembers, creatingGroup, resetNewGroupForm]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete || !clinicId || !activeChannel || working) return;
    setWorking(true);
    try {
      if (pendingDelete.kind === 'message') {
        await ChatService.deleteMessage(clinicId, activeChannel, pendingDelete.messageId);
      } else if (pendingDelete.kind === 'clearChannel') {
        await ChatService.clearChannelMessages(clinicId, activeChannel);
      } else if (pendingDelete.kind === 'deleteChannel') {
        await ChatService.deleteChannel(clinicId, activeChannel);
        setActiveChannel('');
      }
      setPendingDelete(null);
      setShowChannelMenu(false);
    } catch (err) {
      console.error('Erro ao apagar:', err);
      alert('Não foi possível concluir a ação.');
    } finally {
      setWorking(false);
    }
  }, [pendingDelete, clinicId, activeChannel, working]);

  // ── Subscription de EXIBIÇÃO ───────────────────────────────────────────
  // Só do canal ativo, só popula `messages` para renderizar no painel.
  // Não dispara alertas — isso vive no useEffect multi-canal abaixo, que
  // funciona mesmo para canais nos quais o usuário ainda não clicou.
  useEffect(() => {
    if (!clinicId || !activeChannel || !uid) return;
    let unsub: (() => void) | null = null;
    let cancelled = false;

    (async () => {
      try {
        await ChatService.ensureChannelMember(clinicId, activeChannel, uid);
        if (cancelled) return;
        unsub = ChatService.subscribeToMessages(clinicId, activeChannel, (list) => {
          setMessages(list.sort((a, b) => a.timestamp - b.timestamp));
        });
      } catch (err) {
        console.error('FloatingChat channel error:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (unsub) unsub();
      setMessages([]);
    };
  }, [clinicId, activeChannel, uid]);

  // ── Subscription de ALERTAS multi-canal ────────────────────────────────
  // Escuta TODOS os canais onde o usuário é membro (públicos + DMs) em
  // paralelo. Quando chega mensagem de outro usuário, dispara flash visual,
  // som e incrementa o contador de unread. Independente do balão estar
  // aberto ou fechado e independente de qual canal está ativo.
  const membershipKey = useMemo(
    () => channels.filter((c) => c.members?.[uid] === true).map((c) => c.id).sort().join('|'),
    [channels, uid],
  );

  useEffect(() => {
    if (!clinicId || !uid) return;
    const membershipChannels = channels.filter((c) => c.members?.[uid] === true);
    if (membershipChannels.length === 0) return;

    const unsubs: Array<() => void> = [];
    const baselines = new Map<string, number>();

    for (const channel of membershipChannels) {
      const unsub = ChatService.subscribeToMessages(clinicId, channel.id, (list) => {
        const sorted = list.sort((a, b) => a.timestamp - b.timestamp);
        const latestTs = sorted.length > 0 ? sorted[sorted.length - 1].timestamp : 0;

        // Primeiro snapshot: estabelece baseline e NÃO alerta (é histórico).
        if (!baselines.has(channel.id)) {
          baselines.set(channel.id, Math.max(latestTs, Date.now()));
          return;
        }

        const baseline = baselines.get(channel.id)!;
        const incoming = sorted.filter(
          (m) => m.timestamp > baseline && m.senderId !== uid,
        );
        if (incoming.length === 0) return;

        baselines.set(channel.id, incoming[incoming.length - 1].timestamp);

        const hasAttention = incoming.some((m) => m.type === 'attention');
        const panelInvisible = stateRef.current === 'closed' || stateRef.current === 'minimized';
        const tabHidden = typeof document !== 'undefined' && document.hidden;
        const isViewingThis = channel.id === activeChannelRef.current
          && (stateRef.current === 'open' || stateRef.current === 'expanded');

        // Visual: pulsa o balão/barra sempre que NÃO está visualizando o
        // canal exato em painel aberto. Caso contrário não tem por que
        // chamar atenção — a mensagem já apareceu na conversa.
        if (!isViewingThis) setFlashKey((k) => k + 1);

        if (hasAttention) {
          // Pedido de atenção: som forte SEMPRE, mesmo painel aberto
          playChatAttentionPing();
          startTitleFlash(incoming.length);
          // …e o chat ABRE SOZINHO em pop-up no centro da tela de quem foi
          // chamado, já no canal de quem chamou. É esse o sentido do "pedir
          // atenção": não depender de a pessoa notar um badge ou ouvir o som.
          // Se ela JÁ está com esse canal aberto na frente, não precisa —
          // a mensagem apareceu na conversa que ela está lendo.
          if (!isViewingThis) {
            setActiveChannel(channel.id);
            openPopupRef.current();
          }
        } else {
          if (panelInvisible || tabHidden || !isViewingThis) playChatPing();
          if (tabHidden) startTitleFlash(incoming.length);
        }

        // Contador de unread: incrementa quando painel está oculto ou quando
        // a mensagem caiu em outro canal que não o ativo.
        if (panelInvisible || !isViewingThis) {
          setUnreadCount((prev) => prev + incoming.length);
        }
      });
      unsubs.push(unsub);
    }

    return () => {
      unsubs.forEach((u) => u());
    };
    // membershipKey é a string estável de IDs onde o user é membro — evita
    // re-subscrever quando só `dmParticipants` ou outra propriedade muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, uid, membershipKey]);

  // Rola a lista até o fim. Mira direto no container das mensagens (em vez de
  // scrollIntoView num sentinela) pra não arrastar a página por baixo e pra
  // sempre parar no fundo absoluto. Os dois requestAnimationFrame garantem que
  // a mensagem nova já entrou no layout antes de medirmos a altura — sem isso
  // a rolagem acontece antes do DOM crescer e fica "quase no fim".
  const scrollToBottom = useCallback((smooth = true) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      });
    });
  }, []);

  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Ao ABRIR o chat (ou trocar de canal) a lista tem que NASCER na última
  // mensagem: nada de ver o histórico antigo rolando até o fim. Por isso o
  // primeiro posicionamento é síncrono (useLayoutEffect + scrollTop direto,
  // antes do browser pintar) em vez de suave dentro de requestAnimationFrame.
  const initialScrollDoneRef = useRef(false);
  const chatIsOpen = state === 'open' || state === 'expanded';

  // Fechou o painel ou trocou de canal → a próxima abertura volta a "nascer
  // no fundo" em vez de animar.
  useEffect(() => {
    initialScrollDoneRef.current = false;
  }, [activeChannel, chatIsOpen]);

  // Auto-scroll: na abertura, salto instantâneo e invisível; depois disso,
  // a cada mensagem nova desce até o fim — salto quando a última é minha
  // (acabei de enviar), suave quando é recebida.
  useLayoutEffect(() => {
    if (!chatIsOpen) return;
    if (!initialScrollDoneRef.current) {
      if (messages.length === 0) return; // ainda carregando o canal
      const el = messagesContainerRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      initialScrollDoneRef.current = true;
      // Imagens/anexos só ganham altura depois do primeiro layout — regruda
      // no fundo nos dois frames seguintes pra não sobrar um pedaço.
      requestAnimationFrame(() => {
        const a = messagesContainerRef.current;
        if (a) a.scrollTop = a.scrollHeight;
        requestAnimationFrame(() => {
          const b = messagesContainerRef.current;
          if (b) b.scrollTop = b.scrollHeight;
        });
      });
      return;
    }
    const last = messages[messages.length - 1];
    scrollToBottom(last ? last.senderId !== uid : true);
  }, [messages, chatIsOpen, uid, scrollToBottom]);

  // Mark as seen when opening
  useEffect(() => {
    if (state === 'open' || state === 'expanded') {
      lastSeenRef.current = Date.now();
      setUnreadCount(0);
      // Para o flash do título assim que o usuário foca o chat de novo
      stopTitleFlash();
    }
  }, [state]);

  // Minimiza o chat ao clicar fora do painel (aberto ou expandido), pra não
  // ficar tapando o prontuário/agenda enquanto o usuário trabalha por baixo.
  // Os menus/dropdowns são filhos do painel, então cliques neles não contam
  // como "fora". Usa mousedown pra reagir antes da mudança de foco.
  useEffect(() => {
    if (state !== 'open' && state !== 'expanded') return;
    if (alwaysOpen) return; // "manter sempre aberto" → não minimiza ao clicar fora
    if (docked) return; // encaixado ao lado, não cobre nada — não precisa sumir
    if (popup) return; // pop-up tem fundo próprio; quem fecha é o backdrop/Esc
    const handleClickOutside = (e: MouseEvent) => {
      if (isDraggingRef.current) return; // não minimiza no meio de um arraste
      const panel = panelRef.current;
      if (panel && !panel.contains(e.target as Node)) {
        setState('minimized');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [state, alwaysOpen, docked, popup]);

  // Re-clampa a posição livre quando a janela muda de tamanho, pra o chat não
  // ficar (parcialmente) fora da tela. Só re-roda quando entra/sai do modo livre.
  const hasCustomPos = customPos !== null;
  useEffect(() => {
    if (!hasCustomPos) return;
    const onResize = () => {
      const el = panelRef.current;
      const w = el?.offsetWidth ?? 320;
      const h = el?.offsetHeight ?? 56;
      setCustomPos((p) => (p ? clampToViewport(p.x, p.y, w, h) : p));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [hasCustomPos]);

  // Ao abrir/expandir em posição livre, o painel fica bem maior que o FAB —
  // re-clampa pra ele não nascer parcialmente fora da tela.
  useEffect(() => {
    if (!customPosRef.current) return;
    const el = panelRef.current;
    if (!el) return;
    setCustomPos((p) => (p ? clampToViewport(p.x, p.y, el.offsetWidth, el.offsetHeight) : p));
  }, [state]);

  const toggleSound = useCallback(() => {
    setSoundEnabledState((prev) => {
      const next = !prev;
      setChatSoundEnabled(next);
      return next;
    });
  }, []);

  // Mantém a classe de flash por ~2.5s quando uma nova mensagem chega
  const [isFlashing, setIsFlashing] = useState(false);
  useEffect(() => {
    if (flashKey === 0) return;
    setIsFlashing(true);
    const t = window.setTimeout(() => setIsFlashing(false), 2500);
    return () => window.clearTimeout(t);
  }, [flashKey]);

  const doSend = useCallback(async (text: string, lgpd: boolean) => {
    if (!clinicId || !activeChannel || !uid) return;
    setSending(true);
    try {
      await ChatService.sendMessage(
        clinicId, activeChannel, text, uid, userName, lgpd,
        {
          ...(citedPatient && { linkedPatientId: citedPatient.id, patientLinked: citedPatient.name }),
          ...(replyingTo && buildReplyContext(replyingTo)),
        },
      );
      ChatService.clearTyping(clinicId, activeChannel, uid);
      setMsg('');
      setCitedPatient(null);
      setReplyingTo(null);
    } catch (err) {
      console.error('[chat] sendMessage falhou', { clinicId, activeChannel, uid, err });
      const detail = err instanceof Error ? err.message : String(err);
      alert(`Erro ao enviar mensagem: ${detail}`);
    } finally {
      setSending(false);
    }
  }, [clinicId, activeChannel, uid, userName, citedPatient, replyingTo]);

  const handleSend = () => {
    if ((!msg.trim() && !citedPatient) || sending) return;
    doSend(msg.trim() || `📋 ${citedPatient!.name}`, false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !clinicId || !activeChannel || !uid) return;
    try {
      await ChatService.uploadFileAndSend(clinicId, activeChannel, file, uid, userName);
    } catch (err) {
      console.error('[chat] uploadFileAndSend falhou', { clinicId, activeChannel, uid, fileName: file.name, err });
      const detail = err instanceof Error ? err.message : String(err);
      alert(`Falha no upload: ${detail}`);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInputChange = (val: string) => {
    setMsg(val);
    if (clinicId && activeChannel && uid) {
      ChatService.setTyping(clinicId, activeChannel, uid, userName);
    }
  };

  const handleSendAttention = async () => {
    if (!clinicId || !activeChannel || !uid || sending) return;
    try {
      await ChatService.sendAttention(clinicId, activeChannel, uid, userName);
    } catch {
      alert('Não foi possível enviar o pedido de atenção.');
    }
  };

  // Botão "Enviar no chat" da ficha: abre o chat (painel lateral, balão ou
  // pop-up — onde ele estiver) com o paciente já anexado e o cursor no campo.
  useEffect(() => {
    const onCite = (e: Event) => {
      const patient = (e as CustomEvent<Patient>).detail;
      if (!patient?.id) return;
      setCitedPatient(patient);
      if (!popup) {
        if (docked) openView('chat');
        else setState('open');
      }
      // O campo só existe depois que o painel renderiza.
      setTimeout(() => inputRef.current?.focus(), 80);
    };
    window.addEventListener(CHAT_CITE_PATIENT_EVENT, onCite);
    return () => window.removeEventListener(CHAT_CITE_PATIENT_EVENT, onCite);
  }, [popup, docked, openView]);

  // Inicia uma resposta: fixa a mensagem citada e foca o input.
  const startReply = useCallback((message: ChatMessageData) => {
    setReplyingTo(message);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // Rola até a mensagem original e a pisca por ~1.6s. No-op se a original já
  // saiu da janela carregada (a citação segue legível pelo snapshot).
  const jumpToMessage = useCallback((messageId: string) => {
    const el = document.getElementById(`fc-msg-${messageId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMsgId(messageId);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedMsgId(null), 1600);
  }, []);

  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  const clearCustomPos = useCallback(() => {
    setCustomPos(null);
    customPosRef.current = null;
    localStorage.removeItem(CUSTOM_POS_STORAGE_KEY);
  }, []);

  // Escolher um preset de canto descarta a posição livre (volta a ancorar).
  const changePosition = (pos: ChatPosition) => {
    clearCustomPos();
    setPosition(pos);
    localStorage.setItem(POSITION_STORAGE_KEY, pos);
    setShowPositionMenu(false);
  };

  const changeVertical = (v: ChatVertical) => {
    clearCustomPos();
    setVertical(v);
    localStorage.setItem(VERTICAL_STORAGE_KEY, v);
    setShowPositionMenu(false);
  };

  const toggleAlwaysOpen = useCallback(() => {
    setAlwaysOpen((prev) => {
      const next = !prev;
      localStorage.setItem(ALWAYS_OPEN_STORAGE_KEY, next ? '1' : '0');
      return next;
    });
    // Ao ligar com o chat oculto, já traz o painel à frente.
    setState((s) => (s === 'closed' || s === 'minimized' ? 'open' : s));
  }, []);

  // Arrasto livre: começa no mousedown do cabeçalho / FAB / barra minimizada.
  // Distingue clique de arraste por um limiar de movimento; ao soltar, persiste
  // o canto e marca pra suprimir o clique que o navegador dispara em seguida
  // (senão um arraste curto abriria/fecharia o painel).
  const beginDrag = useCallback((e: React.MouseEvent, measureEl: HTMLElement | null) => {
    if (e.button !== 0) return; // só botão esquerdo
    const el = measureEl ?? (e.currentTarget as HTMLElement);
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;
    let moved = false;

    const onMove = (ev: MouseEvent) => {
      if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < DRAG_THRESHOLD) return;
      if (!moved) {
        moved = true;
        isDraggingRef.current = true;
        setIsDragging(true);
      }
      const pos = clampToViewport(ev.clientX - offsetX, ev.clientY - offsetY, w, h);
      customPosRef.current = pos;
      setCustomPos(pos);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (moved) {
        suppressClickRef.current = true;
        if (customPosRef.current) {
          localStorage.setItem(CUSTOM_POS_STORAGE_KEY, JSON.stringify(customPosRef.current));
        }
      }
      isDraggingRef.current = false;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Não inicia arraste quando o mousedown veio de um controle clicável (botões
  // do cabeçalho, X da barra, etc.) — só da área "vazia".
  const isInteractiveTarget = (target: EventTarget | null) =>
    target instanceof Element &&
    !!target.closest('button, a, input, textarea, select, label, [role="button"]');

  // Suprime o clique imediatamente após um arraste (FAB / barra minimizada
  // abrem no onClick — não queremos que abram só por terem sido arrastados).
  const swallowClickAfterDrag = (): boolean => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return true;
    }
    return false;
  };

  // Classes derivadas da âncora vertical. `roundClass` arredonda os cantos
  // do lado OPOSTO à borda em que o chat está grudado (rodapé → arredonda em
  // cima; topo → arredonda embaixo), pra parecer encaixado na borda.
  const vClass = vertical === 'top' ? 'top-0' : 'bottom-0';
  const vClassFab = vertical === 'top' ? 'top-6' : 'bottom-6';
  const roundClass = vertical === 'top' ? 'rounded-b-xl' : 'rounded-t-xl';

  // No modo livre (arrastado) posicionamos por style inline (canto x,y) e
  // arredondamos todos os cantos; senão, valem as classes de âncora de sempre.
  const useCustom = customPos !== null;
  const customStyle: React.CSSProperties | undefined = customPos
    ? { left: customPos.x, top: customPos.y, right: 'auto', bottom: 'auto' }
    : undefined;
  const panelPosClass = useCustom ? '' : `${vClass} ${posClasses[position]}`;
  const fabPosClass = useCustom ? '' : `${vClassFab} ${posClasses[position]}`;
  const panelRound = useCustom ? 'rounded-xl' : roundClass;

  // Hide on /chat page — the full chat is already visible
  if (location.pathname === '/chat') return null;

  if (!clinicId || !uid) return null;

  const activeChannelObj = channels.find((c) => c.id === activeChannel);
  const activeChannelName = activeChannelObj
    ? getChannelDisplayName(activeChannelObj, uid, clinicMembersLookup)
    : activeChannel;
  const isActiveDm = activeChannelObj?.type === 'dm';
  const channelPrefix = isActiveDm ? '@' : '#';
  const publicChannels = channels.filter((c) => c.type !== 'dm');
  const dmChannels = channels.filter((c) => c.type === 'dm');
  const dmCandidates = clinicMembers.filter((m) => m.id !== uid);
  // Owner da clínica sempre pode gerenciar; criador do canal também.
  const canManageChannel =
    !!activeChannelObj && (isOwner || activeChannelObj.createdBy === uid);
  const canDeleteMessage = (senderId: string) => senderId === uid || isOwner;
  const confirmLabel: Record<string, string> = {
    message: 'Apagar esta mensagem?',
    clearChannel: isActiveDm
      ? 'Apagar todo o histórico desta conversa?'
      : 'Apagar todo o histórico deste canal?',
    deleteChannel: 'Apagar este canal e todo o histórico?',
  };

  // Encaixado no painel e com outra vista (ou painel fechado) à mostra: nada
  // flutua — o ícone do trilho (com as não-lidas) é o acesso ao chat.
  if (docked && !dockedVisible && !popup) return null;

  // Opção do menu de posição: mandar o chat pro painel lateral (quando há
  // hospedeiro nesta rota) ou trazê-lo de volta pra flutuar.
  const canDock = chatInDock ? false : dock.hostMounted && wideScreen;
  const dockMenuItem = canDock ? (
    <>
      <div className="border-t border-outline-variant/10 my-1" />
      <button
        onClick={() => { setChatInDock(true); openView('chat'); setShowPositionMenu(false); }}
        className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-on-surface-variant hover:bg-primary/10 transition-colors"
        title="Encaixa o chat no painel lateral direito, no mesmo lugar da agenda do dia"
      >
        <PanelRight size={12} /> Encaixar no painel lateral
      </button>
    </>
  ) : null;

  // FAB button when closed
  if (!docked && !popup && state === 'closed') {
    return (
      <div className={`fixed ${fabPosClass} z-50`} style={customStyle}>
        <button
          onMouseDown={(e) => beginDrag(e, e.currentTarget)}
          onClick={() => { if (swallowClickAfterDrag()) return; setState('open'); }}
          onContextMenu={(e) => { e.preventDefault(); setShowPositionMenu((v) => !v); }}
          className={`relative w-14 h-14 rounded-full bg-primary text-on-primary shadow-lg hover:shadow-xl transition-all flex items-center justify-center ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab hover:scale-105'
          } ${isFlashing ? 'animate-chat-flash' : ''}`}
          title="Arraste para mover · clique direito para posição"
        >
          <MessageCircle size={24} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-error text-on-error text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        {showPositionMenu && (
          <div className={`absolute ${vertical === 'top' ? 'top-16' : 'bottom-16'} ${position === 'left' ? 'left-0' : position === 'center' ? 'left-1/2 -translate-x-1/2' : 'right-0'} bg-surface border border-outline-variant/20 rounded-lg shadow-lg py-1 min-w-[140px]`}>
            <div className="px-3 py-1 text-[10px] font-semibold text-on-surface-variant/60 uppercase tracking-wider">Lado</div>
            {(['left', 'center', 'right'] as ChatPosition[]).map((pos) => (
              <button
                key={pos}
                onClick={() => changePosition(pos)}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors ${
                  pos === position ? 'text-primary font-bold' : 'text-on-surface-variant'
                }`}
              >
                <ArrowLeftRight size={12} />
                {posLabels[pos]}
              </button>
            ))}
            <div className="border-t border-outline-variant/10 my-1" />
            <div className="px-3 py-1 text-[10px] font-semibold text-on-surface-variant/60 uppercase tracking-wider">Altura</div>
            {(['top', 'bottom'] as ChatVertical[]).map((v) => (
              <button
                key={v}
                onClick={() => changeVertical(v)}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors ${
                  v === vertical ? 'text-primary font-bold' : 'text-on-surface-variant'
                }`}
              >
                <ArrowUpDown size={12} />
                {vertLabels[v]}
              </button>
            ))}
            <div className="border-t border-outline-variant/10 my-1" />
            <div className="px-3 py-1 text-[10px] font-semibold text-on-surface-variant/60 uppercase tracking-wider">Comportamento</div>
            <button
              onClick={toggleAlwaysOpen}
              className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors"
            >
              {alwaysOpen ? <Pin size={12} className="text-primary" /> : <PinOff size={12} className="text-on-surface-variant" />}
              <span className={alwaysOpen ? 'text-primary font-bold' : 'text-on-surface-variant'}>Sempre aberto</span>
            </button>
            {useCustom && (
              <button
                onClick={() => { clearCustomPos(); setShowPositionMenu(false); }}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-on-surface-variant hover:bg-primary/10 transition-colors"
              >
                <Move size={12} /> Resetar posição
              </button>
            )}
            {dockMenuItem}
          </div>
        )}
      </div>
    );
  }

  // Minimized bar
  if (!docked && !popup && state === 'minimized') {
    return (
      <div
        className={`fixed ${panelPosClass} z-50 w-72 bg-primary text-on-primary ${panelRound} shadow-lg flex items-center justify-between px-4 py-2.5 hover:brightness-110 transition-all ${
          isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
        } ${isFlashing ? 'animate-chat-flash' : ''}`}
        style={customStyle}
        onMouseDown={(e) => { if (!isInteractiveTarget(e.target)) beginDrag(e, e.currentTarget); }}
        onClick={() => { if (swallowClickAfterDrag()) return; setState('open'); }}
      >
        <div className="flex items-center gap-2 text-sm font-medium truncate">
          <MessageCircle size={16} />
          <span>Chat — {channelPrefix}{activeChannelName}</span>
          {unreadCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-error text-on-error text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setState('closed'); }}
          className="hover:bg-white/20 rounded p-0.5"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Open or expanded panel
  const isExpanded = !docked && !popup && state === 'expanded';
  const panelWidth = isExpanded ? 'w-[500px]' : 'w-80';
  const panelHeight = isExpanded ? 'h-[600px]' : 'h-[420px]';
  // Flutuando LIVRE = nem encaixado no painel, nem em pop-up. Só nesse modo o
  // chat é arrastável e tem âncoras de posição.
  const freeFloating = !docked && !popup;

  // Encaixado: ocupa o painel lateral inteiro (sem arraste, sem cantos, sem
  // sombra) via portal no slot que o hospedeiro expõe. Se o slot ainda não
  // montou (primeiro frame), espera.
  if (docked && !slotEl && !popup) return null;

  const panel = (
    <div
      ref={panelRef}
      className={
        popup
          ? 'relative flex h-full min-h-0 w-full flex-col bg-surface border border-outline-variant/40 rounded-xl overflow-hidden shadow-float'
          : docked
            ? 'relative flex h-full min-h-0 w-full flex-col bg-surface overflow-hidden animate-fade-in'
            : `fixed ${panelPosClass} z-50 ${panelWidth} ${panelHeight} flex flex-col bg-surface border border-outline-variant/20 ${panelRound} overflow-hidden shadow-2xl animate-fade-in ${isDragging ? 'select-none' : ''}`
      }
      style={freeFloating ? customStyle : undefined}
    >
      {/* Header — alça de arraste (só flutuando); cliques em botões não disparam o arraste */}
      <div
        className={`flex items-center justify-between px-3 py-2 bg-primary text-on-primary shrink-0 ${
          freeFloating ? `rounded-t-xl ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}` : ''
        }`}
        onMouseDown={(e) => { if (freeFloating && !isInteractiveTarget(e.target)) beginDrag(e, panelRef.current); }}
      >
        <div className="flex items-center gap-1 min-w-0">
          {freeFloating && <Move size={13} className="opacity-50 shrink-0" aria-hidden="true" />}
          <MessageCircle size={16} />
          <button
            onClick={() => { setShowChannelList((v) => !v); setShowNewDmList(false); setShowNewGroupForm(false); setShowChannelMenu(false); }}
            className="flex items-center gap-1 text-sm font-medium truncate hover:underline"
          >
            {channelPrefix}{activeChannelName}
            <ChevronDown size={12} />
          </button>
          {canManageChannel && (
            <div className="relative">
              <button
                onClick={() => { setShowChannelMenu((v) => !v); setShowChannelList(false); }}
                className="hover:bg-white/20 rounded p-0.5"
                title="Opções do canal"
                aria-label="Opções do canal"
              >
                <MoreVertical size={14} />
              </button>
              {showChannelMenu && (
                <div className="absolute top-7 left-0 bg-surface border border-outline-variant/20 rounded-lg shadow-lg py-1 min-w-[180px] z-20">
                  <button
                    onClick={() => { setPendingDelete({ kind: 'clearChannel' }); setShowChannelMenu(false); }}
                    className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 text-on-surface-variant hover:bg-primary/10 transition-colors"
                  >
                    <Eraser size={12} /> Limpar histórico
                  </button>
                  {!isActiveDm && (
                    <button
                      onClick={() => { setPendingDelete({ kind: 'deleteChannel' }); setShowChannelMenu(false); }}
                      className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 text-error hover:bg-error/10 transition-colors"
                    >
                      <Trash2 size={12} /> Apagar canal
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setShowPending((v) => !v); setShowChannelList(false); }}
            className="relative hover:bg-white/20 rounded p-1"
            title="Mensagens pendentes de resposta"
            aria-label="Mensagens pendentes de resposta"
          >
            <Flag size={14} className={showPending ? 'fill-current' : ''} />
            {visiblePending.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-400 text-black text-[8px] font-bold rounded-full min-w-[14px] h-[14px] px-0.5 flex items-center justify-center leading-none">
                {visiblePending.length > 9 ? '9+' : visiblePending.length}
              </span>
            )}
          </button>
          <button
            onClick={toggleSound}
            className="hover:bg-white/20 rounded p-1"
            title={soundEnabled ? 'Silenciar alerta sonoro' : 'Ativar alerta sonoro'}
            aria-label={soundEnabled ? 'Silenciar alerta sonoro' : 'Ativar alerta sonoro'}
          >
            {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} className="opacity-60" />}
          </button>
          {popup ? null : docked ? (
            <>
              {/* Volta pra agenda no mesmo painel. */}
              {!onAgendaPage && (
              <button
                onClick={() => openView('agenda')}
                className="hover:bg-white/20 rounded-full px-2 py-0.5 flex items-center gap-1 border border-white/30 text-[10px] font-medium"
                title="Trocar para a agenda do dia (Alt+A)"
                aria-label="Trocar para a agenda do dia"
              >
                <CalendarDays size={12} />
                <span>Agenda</span>
              </button>
              )}
              <button
                onClick={() => { setChatInDock(false); setState('open'); }}
                className="hover:bg-white/20 rounded p-1"
                title="Desencaixar — voltar a flutuar por cima da tela"
                aria-label="Desencaixar o chat"
              >
                <PictureInPicture2 size={14} />
              </button>
            </>
          ) : (
          <div className="relative">
            <button
              onClick={() => setShowPositionMenu((v) => !v)}
              className="hover:bg-white/20 rounded-full px-2 py-0.5 flex items-center gap-1 border border-white/30 text-[10px] font-medium"
              title="Mudar posição do chat"
            >
              <ArrowLeftRight size={12} />
              <span>{posLabels[position]}</span>
            </button>
            {showPositionMenu && (
              <div className={`absolute top-8 ${position === 'left' ? 'left-0' : 'right-0'} bg-surface border border-outline-variant/20 rounded-lg shadow-lg py-1 min-w-[140px] z-20`}>
                <div className="px-3 py-1 text-[10px] font-semibold text-on-surface-variant/60 uppercase tracking-wider">Lado</div>
                {(['left', 'center', 'right'] as ChatPosition[]).map((pos) => (
                  <button
                    key={pos}
                    onClick={() => changePosition(pos)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors ${
                      pos === position ? 'text-primary font-bold bg-primary/5' : 'text-on-surface-variant'
                    }`}
                  >
                    <ArrowLeftRight size={12} />
                    {posLabels[pos]}
                  </button>
                ))}
                <div className="border-t border-outline-variant/10 my-1" />
                <div className="px-3 py-1 text-[10px] font-semibold text-on-surface-variant/60 uppercase tracking-wider">Altura</div>
                {(['top', 'bottom'] as ChatVertical[]).map((v) => (
                  <button
                    key={v}
                    onClick={() => changeVertical(v)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors ${
                      v === vertical ? 'text-primary font-bold bg-primary/5' : 'text-on-surface-variant'
                    }`}
                  >
                    <ArrowUpDown size={12} />
                    {vertLabels[v]}
                  </button>
                ))}
                <div className="border-t border-outline-variant/10 my-1" />
                <div className="px-3 py-1 text-[10px] font-semibold text-on-surface-variant/60 uppercase tracking-wider">Comportamento</div>
                <button
                  onClick={toggleAlwaysOpen}
                  className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-primary/10 transition-colors"
                  title="Mantém o chat aberto mesmo ao clicar fora; abre sozinho ao entrar"
                >
                  {alwaysOpen ? <Pin size={12} className="text-primary" /> : <PinOff size={12} className="text-on-surface-variant" />}
                  <span className={alwaysOpen ? 'text-primary font-bold' : 'text-on-surface-variant'}>Sempre aberto</span>
                </button>
                {useCustom && (
                  <button
                    onClick={() => { clearCustomPos(); setShowPositionMenu(false); }}
                    className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 text-on-surface-variant hover:bg-primary/10 transition-colors"
                  >
                    <Move size={12} /> Resetar posição
                  </button>
                )}
                {dockMenuItem}
              </div>
            )}
          </div>
          )}
          <div className="w-px h-4 bg-white/20" />
          {freeFloating && (
            <>
              <button onClick={() => setState(isExpanded ? 'open' : 'expanded')} className="hover:bg-white/20 rounded p-1">
                {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button onClick={() => setState('minimized')} className="hover:bg-white/20 rounded p-1">
                <Minus size={14} />
              </button>
            </>
          )}
          {/* Pop-up no centro da tela — vale flutuando e encaixado no painel. */}
          <button
            onClick={() => (popup ? closePopup() : openPopup())}
            className="hover:bg-white/20 rounded p-1"
            title={popup ? 'Voltar o chat pro lugar de origem (Esc)' : 'Abrir o chat em pop-up no centro da tela'}
            aria-label={popup ? 'Fechar o pop-up do chat' : 'Abrir o chat em pop-up'}
          >
            {popup ? <Shrink size={14} /> : <Expand size={14} />}
          </button>
          <button
            onClick={() => {
              if (popup) { setPopup(false); if (!docked) setState('closed'); return; }
              return docked ? closeDock() : setState('closed');
            }}
            className="hover:bg-white/20 rounded p-1"
            title={docked && !popup ? 'Recolher o painel (Esc)' : 'Fechar'}
            aria-label={docked && !popup ? 'Recolher o painel' : 'Fechar chat'}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Channel selector dropdown */}
      {showChannelList && (
        <div className="absolute top-10 left-3 right-3 bg-surface border border-outline-variant/20 rounded-lg shadow-lg z-10 max-h-72 overflow-y-auto">
          {!showNewDmList && !showNewGroupForm && (
            <>
              <div className="px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-wider text-on-surface-variant/60">
                Canais
              </div>
              {publicChannels.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-on-surface-variant/70">
                  Nenhum canal.
                </div>
              )}
              {publicChannels.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setActiveChannel(c.id); setShowChannelList(false); }}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-primary/10 transition-colors ${
                    c.id === activeChannel ? 'text-primary font-bold' : 'text-on-surface-variant'
                  }`}
                >
                  <Hash size={14} /> {c.name}
                </button>
              ))}

              <div className="border-t border-outline-variant/10 my-1" />
              <div className="px-3 pt-1 pb-1 text-[9px] font-bold uppercase tracking-wider text-on-surface-variant/60">
                Mensagens diretas
              </div>
              {dmChannels.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-on-surface-variant/70">
                  Nenhuma conversa privada.
                </div>
              )}
              {dmChannels.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setActiveChannel(c.id); setShowChannelList(false); }}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-primary/10 transition-colors ${
                    c.id === activeChannel ? 'text-primary font-bold' : 'text-on-surface-variant'
                  }`}
                >
                  <User size={14} /> {getChannelDisplayName(c, uid, clinicMembersLookup)}
                </button>
              ))}

              <div className="border-t border-outline-variant/10 my-1" />
              <button
                onClick={() => setShowNewDmList(true)}
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-primary font-medium hover:bg-primary/10 transition-colors"
              >
                <UserPlus size={14} /> Nova conversa privada
              </button>
              <button
                onClick={() => setShowNewGroupForm(true)}
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-primary font-medium hover:bg-primary/10 transition-colors"
              >
                <Users size={14} /> Novo grupo
              </button>
            </>
          )}

          {showNewDmList && (
            <>
              <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                <span className="text-[9px] font-bold uppercase tracking-wider text-on-surface-variant/60">
                  Escolher pessoa
                </span>
                <button
                  onClick={() => setShowNewDmList(false)}
                  className="text-[10px] text-on-surface-variant hover:text-primary"
                >
                  Voltar
                </button>
              </div>
              {dmCandidates.length === 0 && (
                <div className="px-3 py-3 text-[11px] text-on-surface-variant/70">
                  Nenhum outro usuário disponível no chat ainda.
                </div>
              )}
              {dmCandidates.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleStartDm(m.id, m.name)}
                  disabled={openingDm}
                  className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 text-on-surface-variant hover:bg-primary/10 transition-colors disabled:opacity-50"
                >
                  <User size={14} /> {m.name}
                  {openingDm && <Loader2 size={12} className="ml-auto animate-spin" />}
                </button>
              ))}
            </>
          )}

          {showNewGroupForm && (
            <div className="px-3 py-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold uppercase tracking-wider text-on-surface-variant/60">
                  Novo grupo
                </span>
                <button
                  onClick={resetNewGroupForm}
                  className="text-[10px] text-on-surface-variant hover:text-primary"
                >
                  Voltar
                </button>
              </div>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Nome do grupo (ex: agenda-quarta)"
                maxLength={40}
                className="w-full text-xs bg-surface-container border border-outline/20 rounded px-2 py-1.5 outline-none focus:border-primary/50"
                autoFocus
              />
              <div className="text-[10px] font-semibold text-on-surface-variant/60 mt-1">
                Membros ({Object.values(newGroupMembers).filter(Boolean).length} selecionados)
              </div>
              <div className="max-h-32 overflow-y-auto border border-outline/10 rounded">
                {dmCandidates.length === 0 && (
                  <div className="px-2 py-2 text-[11px] text-on-surface-variant/70">
                    Nenhum outro usuário disponível.
                  </div>
                )}
                {dmCandidates.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-primary/5"
                  >
                    <input
                      type="checkbox"
                      checked={!!newGroupMembers[m.id]}
                      onChange={() =>
                        setNewGroupMembers((prev) => ({ ...prev, [m.id]: !prev[m.id] }))
                      }
                      className="rounded text-primary focus:ring-primary"
                    />
                    <span>{m.name}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={resetNewGroupForm}
                  disabled={creatingGroup}
                  className="px-2 py-1 text-[11px] rounded text-on-surface-variant hover:bg-surface-container"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={creatingGroup || !newGroupName.trim()}
                  className="px-3 py-1 text-[11px] rounded bg-primary text-on-primary hover:brightness-110 disabled:opacity-50 flex items-center gap-1"
                >
                  {creatingGroup && <Loader2 size={10} className="animate-spin" />}
                  Criar grupo
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Painel de pendentes — cobre a área de mensagens */}
      {showPending && (
        <div className="flex-1 overflow-y-auto custom-scrollbar bg-surface-container-lowest flex flex-col">
          <div className="px-3 py-2 border-b border-outline-variant/15 flex items-center justify-between sticky top-0 bg-surface z-20 shrink-0">
            <span className="text-xs font-bold flex items-center gap-1.5 text-on-surface">
              <Flag size={12} className="text-amber-500" /> Pendentes de resposta
            </span>
            <button
              onClick={() => setShowPending(false)}
              className="text-on-surface-variant hover:text-on-surface"
              title="Fechar pendentes"
              aria-label="Fechar pendentes"
            >
              <X size={14} />
            </button>
          </div>
          <PendingMessagesPanel
            compact
            items={visiblePending}
            channelLabel={pendingChannelLabel}
            currentUserId={uid}
            onGoTo={goToPending}
            onResolve={resolveFromPanel}
            resolvingId={resolvingPending}
          />
        </div>
      )}

      {/* Messages */}
      {!showPending && (
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2 custom-scrollbar bg-surface-container-lowest">
        {!isReady && (
          <div className="flex items-center justify-center h-full text-on-surface-variant text-xs">
            <Loader2 size={14} className="mr-1 animate-spin" /> Carregando...
          </div>
        )}

        {isReady && messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-on-surface-variant text-xs">
            Nenhuma mensagem em {channelPrefix}{activeChannelName}.
          </div>
        )}

        {messages.map((m) => {
          // Mensagem de pedido de atenção — banner centralizado, destacado
          if (m.type === 'attention') {
            return (
              <div key={m.id} className="flex justify-center">
                <div className="flex items-center gap-2 bg-warning/15 border border-warning/40 text-on-surface rounded-full px-3 py-1 text-[11px] font-medium">
                  <Bell size={12} className="text-warning shrink-0" />
                  <span>
                    <b>{m.senderName}</b> pediu atenção
                  </span>
                  <span className="text-on-surface-variant/70 text-[9px]">
                    {formatTimeHM(m.timestamp)}
                  </span>
                </div>
              </div>
            );
          }
          const mine = m.senderId === uid;
          const canDelete = canDeleteMessage(m.senderId);
          const isPending = activePendingIds.has(m.id);
          const deleteBtn = canDelete && (
            <button
              onClick={() => setPendingDelete({ kind: 'message', messageId: m.id })}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-on-surface-variant/60 hover:text-error p-1"
              title="Apagar mensagem"
              aria-label="Apagar mensagem"
            >
              <Trash2 size={11} />
            </button>
          );
          const actions = (
            <div className="flex items-center gap-0.5 self-center">
              <button
                onClick={() => startReply(m)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-on-surface-variant/60 hover:text-primary"
                title="Responder"
                aria-label="Responder a esta mensagem"
              >
                <Reply size={11} />
              </button>
              <button
                onClick={() => togglePending(m)}
                className={`transition-opacity p-1 ${
                  isPending
                    ? 'opacity-100 text-amber-500'
                    : 'opacity-0 group-hover:opacity-100 text-on-surface-variant/60 hover:text-amber-500'
                }`}
                title={isPending ? 'Resolver pendência' : 'Marcar como pendente de resposta'}
                aria-label={isPending ? 'Resolver pendência' : 'Marcar como pendente de resposta'}
              >
                <Flag size={11} className={isPending ? 'fill-current' : ''} />
              </button>
              {canPinMural && canPinToMural(m) && (
                <button
                  onClick={() => setMuralFrom(m)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-on-surface-variant/60 hover:text-primary"
                  title="Colar no mural (vira post-it)"
                  aria-label="Colar esta mensagem no mural de recados"
                >
                  <StickyNote size={11} />
                </button>
              )}
              {canRegisterRecord && canRegisterChatInRecord(m) && (
                <button
                  onClick={() => setRecordFrom(m)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-on-surface-variant/60 hover:text-primary"
                  title={
                    m.patientLinked
                      ? `Registrar no prontuário de ${m.patientLinked}`
                      : 'Registrar no prontuário'
                  }
                  aria-label="Registrar esta mensagem no prontuário do paciente"
                >
                  <ClipboardPlus size={11} />
                </button>
              )}
              {deleteBtn}
            </div>
          );
          const highlighted = highlightedMsgId === m.id;
          return (
            <div
              key={m.id}
              id={`fc-msg-${m.id}`}
              className={`flex items-center gap-1 group scroll-mt-4 ${mine ? 'justify-end' : 'justify-start'}`}
            >
              {mine && actions}
              <div
                className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs transition-shadow ${
                  mine
                    ? 'bg-primary text-on-primary rounded-br-none'
                    : 'bg-surface-container text-on-surface rounded-bl-none'
                } ${isPending ? 'ring-1 ring-amber-400/70' : ''} ${
                  highlighted ? 'ring-2 ring-primary ring-offset-1 ring-offset-surface-container-lowest' : ''
                }`}
              >
                {!mine && (
                  <div className="font-bold text-[10px] mb-0.5 opacity-80">{m.senderName}</div>
                )}
                {/* Citação da mensagem respondida (estilo "responder a" do WhatsApp) */}
                {m.replyToId && (
                  <button
                    type="button"
                    onClick={() => jumpToMessage(m.replyToId!)}
                    className={`w-full text-left mb-1 rounded border-l-2 px-1.5 py-0.5 transition-colors ${
                      mine
                        ? 'bg-white/15 border-white/60 hover:bg-white/25'
                        : 'bg-primary/10 border-primary/60 hover:bg-primary/20'
                    }`}
                    title="Ir para a mensagem original"
                  >
                    <div className={`text-[9px] font-bold truncate ${mine ? 'text-on-primary/90' : 'text-primary'}`}>
                      {m.replyToSenderName || 'Mensagem'}
                    </div>
                    <div className={`text-[10px] truncate ${mine ? 'text-on-primary/70' : 'text-on-surface-variant'}`}>
                      {m.replyToText}
                    </div>
                  </button>
                )}
                {m.type === 'image' && m.fileUrl ? (
                  <img
                    src={m.fileUrl}
                    alt={m.fileName || 'image'}
                    className="max-w-full rounded mt-1"
                    onLoad={() => { if (isNearBottom()) scrollToBottom(false); }}
                  />
                ) : m.type === 'file' && m.fileUrl ? (
                  <a href={m.fileUrl} target="_blank" rel="noreferrer" className="underline">{m.fileName || 'Arquivo'}</a>
                ) : (
                  <span>{m.text}</span>
                )}
                {m.hasLgpdWarning && <ShieldAlert size={10} className="inline ml-1 opacity-60" />}
                {m.patientLinked && (
                  <button
                    onClick={() => m.linkedPatientId && navigate(`/pacientes/${m.linkedPatientId}`)}
                    className={`mt-1 flex items-center gap-1 text-[10px] font-medium rounded-full px-1.5 py-0.5 max-w-full transition-colors ${
                      mine
                        ? 'bg-white/20 hover:bg-white/30 text-on-primary'
                        : 'bg-primary/10 hover:bg-primary/20 text-primary'
                    }`}
                    title={`Abrir ficha de ${m.patientLinked}`}
                  >
                    <IdCard size={10} className="shrink-0" />
                    <span className="truncate">{m.patientLinked}</span>
                  </button>
                )}
                <div className={`text-[9px] mt-0.5 ${mine ? 'text-on-primary/60' : 'text-on-surface-variant/60'}`}>
                  {formatTimeHM(m.timestamp)}
                </div>
              </div>
              {!mine && actions}
            </div>
          );
        })}
      </div>
      )}

      {clinicId && (
        <ChatToMuralModal
          clinicId={clinicId}
          message={muralFrom}
          channelName={isActiveDm ? undefined : `#${activeChannelName}`}
          onClose={() => setMuralFrom(null)}
        />
      )}
      {clinicId && canRegisterRecord && (
        <ChatToRecordModal
          clinicId={clinicId}
          message={recordFrom}
          channelLabel={`${channelPrefix}${activeChannelName}`}
          fallbackPatientId={openPatientId}
          onClose={() => setRecordFrom(null)}
        />
      )}

      {/* Confirmação de apagar */}
      {pendingDelete && (
        <div className="px-3 py-2 bg-error/10 border-t border-error/30 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <Trash2 size={14} className="text-error shrink-0 mt-0.5" />
            <span className="text-error font-medium">{confirmLabel[pendingDelete.kind]}</span>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setPendingDelete(null)}
              disabled={working}
              className="px-2 py-1 text-xs rounded bg-surface-container text-on-surface-variant hover:bg-surface-container-high"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={working}
              className="px-2 py-1 text-xs rounded bg-error text-on-error hover:brightness-110 flex items-center gap-1"
            >
              {working && <Loader2 size={10} className="animate-spin" />}
              Apagar
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2 border-t border-outline-variant/10 bg-surface shrink-0">
        {replyingTo && (
          <div className="mb-1.5 flex items-center gap-2 bg-surface-container border-l-4 border-primary rounded-lg px-2 py-1 text-xs">
            <Reply size={13} className="text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold text-primary truncate">
                Respondendo a {replyingTo.senderName}
              </div>
              <div className="text-[10px] text-on-surface-variant truncate">
                {buildReplyContext(replyingTo).replyToText}
              </div>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              className="text-on-surface-variant hover:text-error shrink-0"
              title="Cancelar resposta"
              aria-label="Cancelar resposta"
            >
              <X size={13} />
            </button>
          </div>
        )}
        {citedPatient && (
          <div className="mb-1.5 flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-2 py-1 text-xs">
            <IdCard size={13} className="text-primary shrink-0" />
            <span className="text-on-surface truncate flex-1">
              Paciente: <span className="font-bold">{citedPatient.name}</span>
            </span>
            <button
              onClick={() => setCitedPatient(null)}
              className="text-on-surface-variant hover:text-error shrink-0"
              title="Remover paciente da mensagem"
              aria-label="Remover paciente da mensagem"
            >
              <X size={13} />
            </button>
          </div>
        )}
        <div className="flex items-center gap-1 bg-surface-container rounded-lg px-2 py-1 border border-outline/20 focus-within:border-primary/50 transition-colors">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-1 text-on-surface-variant hover:text-primary"
            title="Anexar arquivo"
          >
            <Paperclip size={14} />
          </button>
          <CitePatientButton
            clinicId={clinicId}
            onPick={setCitedPatient}
            disabled={!isReady || !activeChannel}
            compact
          />
          <button
            onClick={handleSendAttention}
            disabled={!isReady || !activeChannel || sending}
            className="p-1 text-on-surface-variant hover:text-warning disabled:opacity-30"
            title="Pedir atenção — toca o alerta forte E abre o chat em pop-up no centro da tela de quem está neste canal"
            aria-label="Pedir atenção"
          >
            <Bell size={14} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            className="hidden"
            onChange={handleFileUpload}
          />
          <input
            ref={inputRef}
            value={msg}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Mensagem em ${channelPrefix}${activeChannelName}...`}
            className="flex-1 bg-transparent border-none outline-none text-xs px-1 py-1"
            disabled={!isReady || !activeChannel}
          />
          <button
            onClick={handleSend}
            disabled={!isReady || !activeChannel || sending || (!msg.trim() && !citedPatient)}
            className="p-1 text-primary disabled:opacity-30 hover:bg-primary/10 rounded"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );

  // Pop-up: sai do painel/da âncora e vai pro CENTRO da tela, via portal no
  // <body>, com fundo escurecido. O `data-modal-portal` avisa às gavetas que
  // fecham por "clique-fora" (agenda do dia, prontuário) que cliques aqui
  // dentro não são "fora" — senão elas se recolhiam ao usar o chat.
  if (popup) {
    return createPortal(
      <div
        data-modal-portal
        className="fixed inset-0 z-[60] flex items-center justify-center bg-transparent p-4 animate-fade-in"
        onMouseDown={(e) => { if (e.target === e.currentTarget) closePopup(); }}
        role="dialog"
        aria-modal="true"
        aria-label="Chat da clínica"
      >
        <div className="w-full max-w-3xl h-[min(80vh,680px)] flex flex-col">
          {panel}
        </div>
      </div>,
      document.body,
    );
  }

  // Encaixado: entra no slot do painel lateral (o hospedeiro garante que o
  // slot só existe com a vista "chat" à mostra). Flutuando: como sempre.
  return docked && slotEl ? createPortal(panel, slotEl) : panel;
};
